import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createBulkJob,
  listBulkJobs,
  getBulkJobDetail,
  approveBulkProducts,
  cancelBulkJob,
} from '../services/api';
import type {
  BulkJobListResponse,
  BulkJobDetailDto,
  CreateBulkJobRequest,
  CreateBulkJobResponse,
  BulkApproveRequest,
  BulkApproveResponse,
  CancelBulkJobResponse,
} from '../types';

/**
 * T114 [US4]: Bulk job hooks — TanStack React Query wrappers.
 *
 * @see contracts/bulk.md
 * @module
 */

/** List bulk jobs for the current store. */
export function useBulkJobs() {
  return useQuery<BulkJobListResponse>({
    queryKey: ['bulk-jobs'],
    queryFn: () => listBulkJobs(),
    staleTime: 10_000,
  });
}

/** Get detailed status for a specific bulk job. Polls every 3s when running. */
export function useBulkJobDetail(jobId: string | null) {
  return useQuery<BulkJobDetailDto>({
    queryKey: ['bulk-job', jobId],
    queryFn: () => getBulkJobDetail(jobId!),
    enabled: !!jobId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'QUEUED' || status === 'RUNNING' ? 3_000 : false;
    },
    staleTime: 5_000,
  });
}

/** Create a new bulk job. Invalidates the job list on success. */
export function useCreateBulkJob() {
  const queryClient = useQueryClient();

  return useMutation<CreateBulkJobResponse, Error, CreateBulkJobRequest>({
    mutationFn: (body) => createBulkJob(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-jobs'] });
    },
  });
}

/** Approve/reject products for a bulk job. */
export function useApproveBulkProducts(jobId: string) {
  const queryClient = useQueryClient();

  return useMutation<BulkApproveResponse, Error, BulkApproveRequest>({
    mutationFn: (body) => approveBulkProducts(jobId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['bulk-jobs'] });
    },
  });
}

/** Cancel a running bulk job. */
export function useCancelBulkJob() {
  const queryClient = useQueryClient();

  return useMutation<CancelBulkJobResponse, Error, string>({
    mutationFn: (jobId) => cancelBulkJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-jobs'] });
    },
  });
}
